import type { Store } from "@commercetools/platform-sdk";
import { createClient } from "@lib/commercetools/client";
import { LogTag, Logger, ServiceType } from "@lib/monitoring/logger";
import { getSession, signIn } from "next-auth/react";

const requestWithOffset = async (offset = 0, country, isStoreLocatorPage) => {
    let session = await getSession();

    if (!session) {
        try {
            await signIn("anonymous", { redirect: false });
            session = await getSession();
        } catch (error) {
            Logger.error(ServiceType.WEB, "Error occurred during sign-in", {
                tag: LogTag.STORES,
                error,
            });
            throw error;
        }
    }

    const getWhereClause = (country, isStoreLocatorPage) => {
        // Fetching all stores for store locator pages.
        if (!country) return {};
        // Fetching stores per country for store locator for CC pages (PDP, checkout, shopping bag)
        if (!isStoreLocatorPage) {
            return {
                where: `custom(fields(store_CF_Country="${country}") and fields(store_CF_PickupInStore=true))`,
            };
        }
        // US and CA market only want to show stores for that country on the store locator page, the rest of markets will show all the stores
        return {
            where: `custom(fields(store_CF_Country="${country}"))`,
        };
    };

    const client = createClient(session.accessToken);

    return client
        .stores()
        .get({
            queryArgs: {
                limit: 500,
                withTotal: true,
                offset,
                expand: [
                    "custom.fields.store_CF_OperatingHours",
                    "custom.fields.store_CF_ProductMix",
                ],
                localeProjection: "en",
                ...getWhereClause(country, isStoreLocatorPage),
            },
        })
        .execute()
        .then(({ body }) => body)
        .catch((error) => {
            throw error;
        });
};

// We use this to method to fetch a specific store, once we have storeId from C&C
export const requestWithStore = async (offset = 0, storeId = "") => {
    let session = await getSession();

    if (!session) {
        try {
            await signIn("anonymous", { redirect: false });
            session = await getSession();
        } catch (error) {
            Logger.error(ServiceType.WEB, "Error occurred during sign-in", {
                tag: LogTag.STORES,
                error,
            });
            throw error;
        }
    }

    const client = createClient(session.accessToken);

    return client
        .stores()
        .get({
            queryArgs: {
                limit: 500,
                withTotal: false,
                offset,
                expand: [
                    "custom.fields.store_CF_OperatingHours",
                    "custom.fields.store_CF_ProductMix",
                ],
                where: `supplyChannels(id="${`${storeId}`}")`,
                localeProjection: "en",
            },
        })
        .execute()
        .then(({ body }) => body)
        .catch((error) => {
            throw error;
        });
};

export const fetchStores = (country = "", isStoreLocatorPage = false) => {
    return new Promise<Store[]>((resolve, reject) => {
        const data: Store[] = [];
        const batchRequests = (offset = 0) => {
            requestWithOffset(offset, country, isStoreLocatorPage)
                .then((body) => {
                    data.push(...body.results);
                    if (body.count < body.limit) {
                        resolve(data);
                    } else {
                        // There is a limit of 500 per request, so when we have more than the limit, we will  fetch the next batch
                        batchRequests(body.offset + body.limit);
                    }
                })
                .catch((error) => reject(error));
        };

        batchRequests();
    });
};
